"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaTelegramPlane,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaYoutube,
  FaPinterest,
  FaDiscord,
  FaGlobe,
  FaCheckCircle,
} from "react-icons/fa";
import { sendMessage } from "../../actions/sendMessage";
import { submitContactFormEvent } from "../../utils/gtm-events/contact";
import remarkBreaks from "remark-breaks";

// --- Interfaces ---
interface Button {
  id: string;
  text: string;
  type: string;
}

interface Address {
  id: number;
  text?: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  Phone?: string;
  email?: string;
}

interface SocialLink {
  id: number;
  text: string;
  social: string;
  url: string;
  newTab: boolean;
}

interface DropdownLink {
  data: {
    label: string;
    url: string;
  };
}

interface LinkItem {
  id: number;
  text: string;
  url: string;
  dropdownLinks?: DropdownLink[];
  newTab?: boolean;
}

interface ContactProps {
  data: {
    id: number;
    title: string;
    description: string | null;
    isLive: boolean;
    useH1Tag: boolean;
    mapUrl?: string;
    layout: string;
    button: Button[];
    link: LinkItem[];
    socialLinks: SocialLink[];
    addresses: Address[];
  };
}

// --- Helper for Social Icons ---
const getSocialIcon = (type: string) => {
  switch (type) {
    case "INSTAGRAM": return <FaInstagram />;
    case "FACEBOOK": return <FaFacebookF />;
    case "TWITTER": return <FaTwitter />;
    case "LINKEDIN": return <FaLinkedinIn />;
    case "YOUTUBE": return <FaYoutube />;
    case "PINTEREST": return <FaPinterest />;
    case "DISCORD": return <FaDiscord />;
    case "WEBSITE": return <FaGlobe />;
    default: return <FaGlobe />;
  }
};


// --- Helper for Social Colors (NEW) ---
const getSocialStyles = (type: string) => {
  const base = "transition-all duration-300 border";
  switch (type) {
    case "INSTAGRAM":
      return `${base} text-pink-600 bg-pink-50 border-pink-200 hover:bg-pink-600 hover:border-pink-600 hover:text-white`;
    case "FACEBOOK":
      return `${base} text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-600 hover:border-blue-600 hover:text-white`;
    case "TWITTER":
      return `${base} text-sky-500 bg-sky-50 border-sky-200 hover:bg-sky-500 hover:border-sky-500 hover:text-white`;
    case "LINKEDIN":
      return `${base} text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-700 hover:border-blue-700 hover:text-white`;
    case "YOUTUBE":
      return `${base} text-red-600 bg-red-50 border-red-200 hover:bg-red-600 hover:border-red-600 hover:text-white`;
    case "PINTEREST":
      return `${base} text-red-700 bg-red-50 border-red-200 hover:bg-red-700 hover:border-red-700 hover:text-white`;
    case "DISCORD":
      return `${base} text-indigo-500 bg-indigo-50 border-indigo-200 hover:bg-indigo-500 hover:border-indigo-500 hover:text-white`;
    default:
      return `${base} text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-800 hover:border-gray-800 hover:text-white`;
  }
};

// --- Custom Markdown Components ---
const markdownComponents = {
  p: ({ children }: any) => (
    <p className="text-gray-600 text-sm leading-relaxed mb-4 last:mb-0">
      {children}
    </p>
  ),
  a: ({ href, children }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--secondary)] font-medium hover:underline transition-colors"
    >
      {children}
    </a>
  ),
  strong: ({ children }: any) => (
    <strong className="font-bold text-red-500">{children} <br /></strong>
  ),
  ul: ({ children }: any) => (
    <ul className="list-disc list-outside ml-4 mb-4 text-gray-600 text-sm space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol className="list-decimal list-outside ml-4 mb-4 text-gray-600 text-sm space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }: any) => (
    <li className="pl-1">{children}</li>
  ),
};

export default function Layout7({ data }: ContactProps) {
  if (!data || data?.isLive === false) return null;

  const addr = data.addresses?.[0] || {};
  const socials = data.socialLinks || [];
  const btnText = data.button?.[0]?.text || "Send Message";

  const showMap = data.mapUrl && (data.mapUrl.includes("embed") || data.mapUrl.includes("google"));

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      const res = await sendMessage(formData);
      if (res.type === "success") {
        submitContactFormEvent({
          formName: "Contact Us",
          formLocation: window.location.pathname,
        });
        setStatus("success");
      } else {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 3000);
      }
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <section className="relative py-20 lg:py-8 bg-gradient-to-b from-[#F472B6]/10 to-[#F0F7FF] border-t border-gray-200">

      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          {data.useH1Tag ? (
            <h1 className="text-2xl font-bold text-red-600 tracking-tight sm:text-3xl mb-4">
              {data.title}
            </h1>
          ) : (
            <h2 className="text-2xl font-bold text-red-600 tracking-tight sm:text-3xl mb-4">
              {data.title}
            </h2>
          )}
          {data.description && (
            <p className="text-md text-gray-600 leading-relaxed">
              {data.description}
            </p>
          )}
        </div>

        {/* Content Grid */}
        <div className="flex gap-8 lg:gap-12 items-start justify-center">

          {/* Left Column: Contact Info & Map */}
          <div className="lg:col-span-5 space-y-8 h-full rounded-2xl shadow-lg border border-gray-100">

            {/* Info Card */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Details</h3>
              <div className="space-y-6">
                <div className="space-y-6">
                  {/* Address */}
                  {addr.address && (
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-600 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300 flex-shrink-0">
                        <FaMapMarkerAlt className="size-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-red-600 text-sm uppercase tracking-wide">Visit Us</p>
                        <p className="text-gray-600 mt-1 text-sm leading-relaxed">
                          {addr.address}<br />
                          {addr.city}, {addr.state}<br />
                          {addr.country} - {addr.postalCode}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {addr.email && (
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600 group-hover:bg-sky-600 group-hover:text-white transition-colors duration-300 flex-shrink-0">
                        <FaEnvelope className="size-4" />
                      </div>
                      <div className="selection:bg-sky-600 selection:text-white">
                        <p className="font-semibold text-sky-600 text-sm uppercase tracking-wide">Email</p>
                        <a href={`mailto:${addr.email}`} className="text-gray-600 mt-1 text-sm hover:text-sky-600 transition-colors block">
                          {addr.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Phone */}
                  {addr.Phone && (
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-lime-50 flex items-center justify-center text-lime-600 group-hover:bg-lime-600 group-hover:text-white transition-colors duration-300 flex-shrink-0">
                        <FaPhone className="size-4" />
                      </div>
                      <div className="selection:bg-lime-600 selection:text-white">
                        <p className="font-semibold text-lime-600 text-sm uppercase tracking-wide">Phone</p>
                        <a href={`tel:${addr.Phone}`} className="text-gray-600 mt-1 text-sm hover:text-lime-600 transition-colors block">
                          +91 {addr.Phone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Text */}
              {addr.text && (
                <div className="py-4 border-b border-gray-100">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={markdownComponents}
                  >
                    {addr.text.replace(/\\n/g, "\n")}
                  </ReactMarkdown>
                </div>
              )}

              {/* Social Links (UPDATED) */}
              {socials.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-500 mb-4">Follow us</p>
                  <div className="flex gap-3 flex-wrap">
                    {socials.map((item) => (
                      <a
                        key={item.id}
                        href={item.url}
                        target={item.newTab ? "_blank" : "_self"}
                        aria-label={item.text}
                        // Using helper function for dynamic colors
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${getSocialStyles(item.social)}`}
                      >
                        <span className="text-lg">
                          {getSocialIcon(item.social)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="h-full lg:col-span-7 bg-white rounded-2xl shadow-lg border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[var(--secondary)] to-[var(--primary)]" />

            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="min-h-[400px] flex flex-col items-center justify-center text-center"
                >
                  <div className="w-20 h-20 bg-lime-50 rounded-full flex items-center justify-center mb-6">
                    <FaCheckCircle className="text-lime-500 size-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 max-w-sm mx-auto mb-8">
                    Thank you for reaching out. Our team will review your message and get back to you shortly.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-6 relative z-10"
                >
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                      Send us a <span className="text-transparent bg-clip-text bg-red-500">message</span>
                    </h3>
                    <p className="text-gray-500 mt-2 text-base">We'd love to hear from you. Fill out the colorful form below!</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">

                    {/* Name Field - sky THEME */}
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-sky-400 group-focus-within:text-sky-600 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                      </div>
                      <input
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-sky-50/50 border border-sky-300 rounded-xl text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:bg-white transition-all peer"
                        placeholder="Full Name"
                        id="name"
                      />
                      <label
                        htmlFor="name"
                        className="absolute left-12 -top-2.5 bg-white px-2 rounded-md text-xs font-bold text-sky-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:bg-transparent peer-placeholder-shown:top-4 peer-focus:-top-2.5 peer-focus:text-sky-600 peer-focus:bg-white peer-focus:text-xs"
                      >
                        Full Name
                      </label>
                    </div>

                    {/* Phone Field - lime THEME */}
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-lime-500 group-focus-within:text-lime-600 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" /></svg>
                      </div>
                      <input
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-lime-50/50 border border-lime-300 rounded-xl text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-lime-500/20 focus:border-lime-500 focus:bg-white transition-all peer"
                        placeholder="Phone Number"
                        id="mobile"
                      />
                      <label
                        htmlFor="mobile"
                        className="absolute left-12 -top-2.5 bg-white px-2 rounded-md text-xs font-bold text-lime-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:bg-transparent peer-placeholder-shown:top-4 peer-focus:-top-2.5 peer-focus:text-lime-600 peer-focus:bg-white peer-focus:text-xs"
                      >
                        Phone Number
                      </label>
                    </div>
                  </div>

                  {/* Email Field - orange THEME */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-orange-400 group-focus-within:text-orange-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                    </div>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-orange-50/50 border border-orange-300 rounded-xl text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 focus:bg-white transition-all peer"
                      placeholder="Email Address"
                      id="email"
                    />
                    <label
                      htmlFor="email"
                      className="absolute left-12 -top-2.5 bg-white px-2 rounded-md text-xs font-bold text-orange-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:bg-transparent peer-placeholder-shown:top-4 peer-focus:-top-2.5 peer-focus:text-orange-600 peer-focus:bg-white peer-focus:text-xs"
                    >
                      Email Address
                    </label>
                  </div>

                  {/* Message Field - yellow THEME */}
                  <div className="relative group">
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-yellow-50/50 border border-yellow-300 rounded-xl text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 focus:bg-white transition-all peer resize-none"
                      placeholder="Your Message"
                      id="message"
                    />
                    <label
                      htmlFor="message"
                      className="absolute left-4 -top-2.5 bg-white px-2 rounded-md text-xs font-bold text-yellow-500 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:bg-transparent peer-placeholder-shown:top-4 peer-focus:-top-2.5 peer-focus:text-yellow-600 peer-focus:bg-white peer-focus:text-xs"
                    >
                      Your Message
                    </label>
                  </div>

                  {/* Submit Button - MULTICOLOR GRADIENT */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full py-4 px-6 rounded-xl bg-purple-500 text-white font-bold text-lg shadow-lg shadow-purple-500/30 hover:shadow-lg hover:translate-y-[-2px] active:translate-y-[0px] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden group"
                    >
                      {/* Animated Shine */}
                      <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />

                      {status === "submitting" ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <FaTelegramPlane className="size-5" />
                          <span>{btnText}</span>
                        </>
                      )}
                    </button>

                    {status === "error" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm text-center mt-4 bg-red-50 py-3 rounded-lg border border-red-100 flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        Something went wrong. Please check your connection.
                      </motion.div>
                    )}
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

        </div>

        {/* Map */}
        {showMap && (
          <div className="max-w-6xl w-full h-[500px] mx-auto mt-8 rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
            <iframe
              src={data.mapUrl || ""}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.3)" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map"
            />
          </div>
        )}
      </div>
    </section>
  );
}
